<template>
  <div class="personnel-worklogs">
    <OmxList
        :data="personnelWorklogsData"
        :loading="isLoading"
        :show-search="false"
        empty-icon="account-hard-hat"
    >
      <template #actions v-if="currentUserAccess('ProjectLogs', UserActions.CREATE, project_id)">
        <b-button type="is-primary" icon-left="plus-thick" @click="addNewPersonalWorklogClicked">
          {{ $t('personnel-worklogs.new-personal-log-button') }}
        </b-button>
      </template>

      <b-table-column field="date" :label="$t('personnel-worklogs.personal-logdate-column')" v-slot="props">
        {{ props.row.date }}
      </b-table-column>
      <b-table-column field="name" :label="$t('personnel-worklogs.personal-fullname-column')" v-slot="props">
        {{ props.row.fullname }}
      </b-table-column>
      <b-table-column field="agency" :label="$t('personnel-worklogs.personal-agency-column')" v-slot="props">
        {{ props.row.agency }}
      </b-table-column>
      <b-table-column field="hours" :label="$t('personnel-worklogs.personal-hours-column')" v-slot="props">
        {{ getFormattedTimeHour(props.row.personalLog.quantity) }}
      </b-table-column>
      <b-table-column field="total" :label="$t('personnel-worklogs.personal-total-column')" v-slot="props">
        {{ getFormattedTimeHour(props.row.total) }}
      </b-table-column>
      <b-table-column field="text" :label="$t('personnel-worklogs.personal-text-column')" v-slot="props">
        {{ props.row.personalLog.text }}
      </b-table-column>
      <b-table-column
          v-if="currentUserAccess('ProjectLogs', UserActions.DELETE, project_id)"
          :label="$t('personnel-worklogs.actions-column')" centered v-slot="props" width="10%">
        <b-tooltip :label="$t('common.edit')" position="is-top">
          <b-button type="is-primary" size="is-small" class="m-1" icon-left="pencil"
                    @click="editPersonalWorklogClicked(props.row.personalLog)" />
        </b-tooltip>
        <b-tooltip :label="$t('common.delete')" position="is-top">
          <b-button type="is-danger" size="is-small" class="m-1" icon-left="delete"
                    @click="deletePersonalWorklogClicked(props.row.personalLog)" />
        </b-tooltip>
      </b-table-column>
    </OmxList>

    <b-modal
        v-model="isPersonalWorklogModalActive"
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal>
      <template #default="props">
        <PersonalWorklog :project_id="project_id" :personnel_data="personnelData" :log_data="selected_log" :selected_date="selected_date"
                         @close="props.close"
                         @personalWorklogAdded="readPersonnelWorklogsData" @personalWorklogUpdated="readPersonnelWorklogsData" />
      </template>
    </b-modal>
  </div>
</template>

<script>

import firebase from "firebase/app";
import PersonalWorklog from "@/components/PersonalWorklog";
import OmxList from "@/components/OmxList.vue";

export default {
  name: "PersonnelWorklogs",
  components: {
    PersonalWorklog,
    OmxList
  },
  props: {
    'project_id': {
      type: String,
    },
    'selected_date': {}
  },
  data() {
    return {
      modelDate: new Date(),
      selectedUntilDate: new Date((new Date()).setHours(23, 59, 59, 0)),
      selectedFromDate: new Date((new Date()).setHours(0, 0, 0, 0)),
      personnelData: [],
      personnelWorklogsData: [],
      isLoading: false,
      isPersonalWorklogModalActive: false,
      selected_log: {}
    }
  },
  watch: {
    selected_date: function () {
      this.dateSelectClicked(this.selected_date)
    }
  },
  methods: {
    readPersonnelData: function () {
      this.personnelData = [];
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getPersonnelFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getPersonnelFunction({action: "get_personnel", project_id: this.project_id, fill_meta: true})
          .then((result) => {
            if (result.data.personnel.length > 0) {
              result.data["personnel"].forEach((personal) => {
                this.personnelData.push({
                  personal: personal,
                  fullname: this.getFullName(personal.name),
                });
              });
            }
          })
          .catch((error) => {
            this.showError(error.message)
            console.log("Error getting personnel:", error?.code);
          });
    },
    readPersonnelWorklogsData: function () {
      this.isPersonalWorklogModalActive = false;
      this.isLoading = true;
      this.personnelWorklogsData = [];
      this.selected_log = {};
      this.selected_log.document_id = "new"
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getPersonnelLogsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payloadData = {
        action: "get_logs",
        project_id: this.project_id,
        personal_id: "*",
        fill_meta: true,
        filters: [['date', '>', this.selectedFromDate.toISOString()], ['date', '<', this.selectedUntilDate.toISOString()]]
      }
      getPersonnelLogsFunction(payloadData)
          .then((result) => {
            if (result.data.personnellogs.length > 0) {
              result.data["personnellogs"].forEach((personalLog) => {
                this.personnelWorklogsData.push({
                  personalLog: personalLog,
                  total: personalLog.parent.logged_quantities,
                  fullname: this.getFullName(personalLog.parent.name),
                  agency: personalLog.parent.agency,
                  date: this.getFormattedDate(personalLog.date),
                  document_id: personalLog.document_id
                });
              });
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message)
            console.log("Error getting logs:", error?.code);
          });
    },
    deletePersonalWorklog: function (personalLog) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deletePersonalLogFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deletePersonalLogFunction({
        action: "delete_log",
        project_id: personalLog.project_id,
        personal_id: personalLog.personal_id,
        document_id: personalLog.document_id,
      }).then(() => {
        this.isLoading = false;
        this.showSuccess(this.$t('messages.personal-log-deleted'));
        this.readPersonnelWorklogsData()
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error deleting worklog:", error?.code);
      });
    },
    /*
     UI Events
     */
    dateSelectClicked: function (date) {
      const fromDate = date.setHours(0, 0, 0, 0)
      const untilDate = date.setHours(23, 59, 59, 0)
      this.selectedFromDate = new Date(fromDate)
      this.selectedUntilDate = new Date(untilDate)
      this.readPersonnelWorklogsData();
    },
    addNewPersonalWorklogClicked: function () {
      this.selected_log = {};
      this.selected_log.document_id = "new";
      this.isPersonalWorklogModalActive = true;
    },
    editPersonalWorklogClicked: function (personalLog) {
      this.selected_log = personalLog;
      this.isPersonalWorklogModalActive = true;
    },
    async deletePersonalWorklogClicked(personalLog) {
      const ok = await this.$omxConfirm({
        title: this.$t('personnel-worklogs.delete-log-title').toString(),
        message: this.$t('personnel-worklogs.delete-log-message').toString(),
        confirmText: this.$t('personnel-worklogs.delete-log-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deletePersonalWorklog(personalLog);
    }
  },
  mounted() {
    this.readPersonnelData()
    this.readPersonnelWorklogsData();
  }
};
</script>

<style scoped>
.personnel-worklogs {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
